import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Loader2, CheckCircle2, Copy, Check, MessageCircle, Download, Share2, Users, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { PhoneInput } from '../components/PhoneInput';
import { ESTADOS } from '../components/StateCitySelect';
import cidadesBrasil from '../data/cidades-brasil.json';
import { useQrCode } from '../hooks/useQrCode';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { useGTM } from '../hooks/useGTM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LeaderSignupPage = ({ mode = 'public' }) => {
  // mode: 'public' = /:slug/cadastrar
  //       'invite' = /:slug/cl/:token
  //       'dobrada' = /:slug/d/:dobrada_slug/cadastrar
  const { slug, token, dobrada_slug } = useParams();
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const buildShareUrl = (t) => `${apiBase}/api/og/r/${slug}/${t}`;
  const infoUrl =
    mode === 'invite'
      ? `${BACKEND_URL}/api/public/leader-invite/${slug}/${token}`
      : mode === 'dobrada'
        ? `${BACKEND_URL}/api/public/dobrada-signup/${slug}/${dobrada_slug}`
        : `${BACKEND_URL}/api/public/leader-signup/${slug}`;
  const submitUrl =
    mode === 'invite'
      ? `${BACKEND_URL}/api/public/leader-invite/${slug}/${token}`
      : mode === 'dobrada'
        ? `${BACKEND_URL}/api/public/dobrada-signup/${slug}/${dobrada_slug}`
        : `${BACKEND_URL}/api/public/leader-signup/${slug}`;
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [selectedLinkIdx, setSelectedLinkIdx] = useState(0);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  // Meta Pixel (tenant-scoped, isolated).
  const { trackEvent } = useMetaPixel(info?.meta_pixel_id, info?.meta_pixel_test_event_code);
  // GTM (same tenant-scoped isolation).
  useGTM(info?.gtm_container_id);
  const [formData, setFormData] = useState({
    nome_completo: '',
    apelido: '',
    telefone: '',
    data_nascimento: '',
    estado: '',
    cidade: '',
    bairro: '',
    tipo: '',
    observacoes: '',
    website: '', // honeypot
  });
  const [copied, setCopied] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(false);
  // When backend returns the new `links` array, use the selected link's data.
  // Falls back to the flat fields for backward-compat with the legacy success shape.
  const activeLink = (result?.links && result.links[selectedLinkIdx]) || result || null;
  const activeToken = activeLink?.token;
  const activeInviteToken = activeLink?.leader_invite_token;
  const liveShareUrl = activeToken ? buildShareUrl(activeToken) : '';
  const liveInviteUrl = activeInviteToken
    ? `${apiBase}/api/og/cl/${slug}/${activeInviteToken}`
    : '';
  const liveQrDataUrl = useQrCode(liveShareUrl);
  const liveInviteQrDataUrl = useQrCode(liveInviteUrl);

  // ----- Mobile-friendly state/city using native <select> -----
  const estadosVisiveis = useMemo(() => {
    const allowed = info?.allowed_states;
    if (!allowed || !allowed.length) return ESTADOS;
    const set = new Set(allowed);
    const filtered = ESTADOS.filter((e) => set.has(e.sigla));
    return filtered.length ? filtered : ESTADOS;
  }, [info?.allowed_states]);

  const cidadesDoEstado = useMemo(() => {
    if (!formData.estado || !cidadesBrasil[formData.estado]) return [];
    return cidadesBrasil[formData.estado];
  }, [formData.estado]);

  // ----- Defensive: filter out empty/null values from leader types -----
  const tiposValidos = useMemo(
    () => (info?.lideranca_tipos || []).filter((t) => typeof t === 'string' && t.trim().length > 0),
    [info?.lideranca_tipos]
  );

  useEffect(() => {
    fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, dobrada_slug]);

  const fetchInfo = async () => {
    setLoadingInfo(true);
    try {
      const { data } = await axios.get(infoUrl);
      setInfo(data);
      // Update <head> metadata dynamically for browsers / crawlers that execute JS
      const dynamicTitle = data?.signup_title || `Seja uma liderança em ${data?.tenant_name || ''}`.trim();
      const dynamicDesc =
        data?.signup_subtitle ||
        `Cadastre-se como liderança em ${data?.tenant_name || 'nossa rede'} e ganhe um link personalizado para convidar apoiadores.`;
      const dynamicImage = data?.signup_hero_image_url || '';
      if (typeof document !== 'undefined' && dynamicTitle) {
        document.title = dynamicTitle;
        const setMeta = (selector, attr, value) => {
          if (!value) return;
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement('meta');
            const [, key, val] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
            if (key && val) el.setAttribute(key, val);
            document.head.appendChild(el);
          }
          el.setAttribute(attr, value);
        };
        setMeta('meta[name="description"]', 'content', dynamicDesc);
        setMeta('meta[property="og:title"]', 'content', dynamicTitle);
        setMeta('meta[property="og:description"]', 'content', dynamicDesc);
        if (dynamicImage) setMeta('meta[property="og:image"]', 'content', dynamicImage);
        setMeta('meta[name="twitter:title"]', 'content', dynamicTitle);
        setMeta('meta[name="twitter:description"]', 'content', dynamicDesc);
        if (dynamicImage) setMeta('meta[name="twitter:image"]', 'content', dynamicImage);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg;
      if (status === 404) {
        msg = 'Esta URL de cadastro é inválida ou o cliente não está mais ativo.';
      } else if (status === 403) {
        msg = 'O cadastro de lideranças está temporariamente desativado. Procure a coordenação.';
      } else if (status === 503) {
        msg = 'O cadastro ainda não foi liberado pelo administrador. Tente novamente mais tarde.';
      } else {
        msg = typeof detail === 'string' ? detail : 'Cadastro indisponível';
      }
      setError(msg);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome_completo || !formData.telefone || !formData.cidade || !formData.tipo) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    // Convert DD/MM/YYYY → ISO YYYY-MM-DD for backend
    let isoBirth = '';
    if (formData.data_nascimento) {
      const m = formData.data_nascimento.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!m) {
        toast.error('Data de nascimento inválida. Use o formato dia/mês/ano.');
        return;
      }
      const [, dd, mm, yyyy] = m;
      const day = parseInt(dd, 10);
      const month = parseInt(mm, 10);
      const year = parseInt(yyyy, 10);
      const dt = new Date(year, month - 1, day);
      if (
        dt.getFullYear() !== year ||
        dt.getMonth() !== month - 1 ||
        dt.getDate() !== day ||
        year < 1920 ||
        dt > new Date()
      ) {
        toast.error('Data de nascimento inválida.');
        return;
      }
      isoBirth = `${yyyy}-${mm}-${dd}`;
    }
    setSubmitting(true);
    setError('');
    try {
      // Capture geolocation non-blocking (user can deny → submission proceeds without coords)
      const geo = await new Promise((resolve) => {
        if (!('geolocation' in navigator)) return resolve(null);
        let settled = false;
        const done = (val) => { if (!settled) { settled = true; resolve(val); } };
        try {
          navigator.geolocation.getCurrentPosition(
            (pos) => done({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              geo_accuracy: pos.coords.accuracy,
            }),
            () => done(null),
            { timeout: 6000, maximumAge: 60000, enableHighAccuracy: false }
          );
          setTimeout(() => done(null), 7000);
        } catch { done(null); }
      });
      const { data } = await axios.post(
        submitUrl,
        { ...formData, data_nascimento: isoBirth || null, ...(geo || {}) }
      );
      if (data?.recovered) {
        toast.success('Encontramos seu cadastro. Aqui estão seus links!');
      } else {
        // Fire CompleteRegistration only for genuinely new leader signups.
        trackEvent('CompleteRegistration', {
          content_name: 'Leader Signup',
          content_category: 'lideranca',
        });
      }
      setSelectedLinkIdx(0);
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let friendlyMsg;
      if (status === 409) {
        friendlyMsg = typeof detail === 'string'
          ? detail
          : 'Este telefone já está cadastrado como liderança. Procure a coordenação para verificar.';
      } else if (status === 429) {
        friendlyMsg = 'Você tentou se cadastrar muitas vezes em pouco tempo. Aguarde alguns minutos e tente novamente.';
      } else if (status === 403) {
        friendlyMsg = 'O cadastro de novas lideranças está temporariamente desativado. Procure a coordenação.';
      } else if (status === 503) {
        friendlyMsg = 'O cadastro está indisponível no momento porque o administrador ainda não finalizou a configuração. Tente mais tarde.';
      } else if (status === 404) {
        friendlyMsg = 'Esta URL de cadastro é inválida ou foi desativada.';
      } else if (status === 422 || status === 400) {
        friendlyMsg = typeof detail === 'string'
          ? detail
          : 'Verifique se todos os campos foram preenchidos corretamente.';
      } else {
        friendlyMsg = typeof detail === 'string' ? detail : 'Erro ao cadastrar. Tente novamente.';
      }
      setError(friendlyMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const openRecovery = () => {
    setRecoveryError('');
    setRecoveryPhone(formData.telefone || '');
    setRecoveryOpen(true);
  };

  const handleRecovery = async (e) => {
    e?.preventDefault?.();
    setRecoveryError('');
    const digits = (recoveryPhone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      setRecoveryError('Informe um telefone válido com DDD.');
      return;
    }
    setRecoverySubmitting(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/public/leader-recovery/${slug}`,
        { telefone: recoveryPhone }
      );
      setSelectedLinkIdx(0);
      setResult(data);
      setRecoveryOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Tudo certo! Aqui estão seus links.');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg;
      if (status === 404) {
        msg = typeof detail === 'string' ? detail : 'Não encontramos uma liderança com este telefone.';
      } else if (status === 429) {
        msg = 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
      } else if (status === 403) {
        msg = typeof detail === 'string' ? detail : 'Sua liderança está desativada. Procure a coordenação.';
      } else if (status === 422) {
        msg = typeof detail === 'string' ? detail : 'Telefone inválido.';
      } else {
        msg = typeof detail === 'string' ? detail : 'Não foi possível recuperar seus links agora.';
      }
      setRecoveryError(msg);
    } finally {
      setRecoverySubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!activeToken) return;
    try {
      await navigator.clipboard.writeText(buildShareUrl(activeToken));
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Não foi possível copiar');
    }
  };

  const copyInviteLink = async () => {
    if (!liveInviteUrl) return;
    try {
      await navigator.clipboard.writeText(liveInviteUrl);
      setCopiedInvite(true);
      toast.success('Link de convite de lideranças copiado!');
      setTimeout(() => setCopiedInvite(false), 2000);
    } catch (err) {
      toast.error('Não foi possível copiar');
    }
  };

  const downloadQR = (kind = 'supporter') => {
    const isInvite = kind === 'invite';
    const dataUrl = isInvite ? liveInviteQrDataUrl : (liveQrDataUrl || activeLink?.qr_code);
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = isInvite
      ? `qrcode-lider-${activeInviteToken}.png`
      : `qrcode-apoiador-${activeToken}.png`;
    link.click();
  };

  // ===== Render: loading =====
  if (loadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // ===== Render: error (unavailable) =====
  if (error && !info && !result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4 text-2xl font-black">
            !
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Cadastro indisponível</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  // ===== Render: success — 3 blocks (Apoiadores, Lideranças, Canal) =====
  if (result) {
    const tenantColor = result.primary_color || '#1e3a8a';
    const isRecovered = !!result.recovered;
    const linkOptions = Array.isArray(result.links) ? result.links : [];
    const hasMultiple = linkOptions.length > 1;
    const supporterWa = activeToken
      ? `https://wa.me/?text=${encodeURIComponent(
          `Entre na rede ${result.tenant_name}! Clique: ${buildShareUrl(activeToken)}`
        )}`
      : '';
    const inviteWa = liveInviteUrl
      ? `https://wa.me/?text=${encodeURIComponent(
          `Convido você para ser uma liderança em ${result.tenant_name}! Cadastre-se: ${liveInviteUrl}`
        )}`
      : '';
    const destinationName = activeLink?.destination_name || result.destination_name;
    const destinationUrl = activeLink?.destination_url || result.destination_url;
    return (
      <div
        className="min-h-screen p-4 sm:p-6"
        style={{ background: `linear-gradient(135deg, ${tenantColor}10, ${tenantColor}05)` }}
      >
        <div className="max-w-2xl mx-auto pt-6 space-y-4">
          {/* Header */}
          <div className="text-center space-y-2 mb-2">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto"
              style={{ background: tenantColor }}
            >
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="success-heading">
              {isRecovered ? 'Bem-vindo(a) de volta!' : 'Cadastro concluído!'}
            </h1>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              {result.welcome_message}
            </p>
          </div>

          {/* Link picker — shown only when leader has multiple active links */}
          {hasMultiple && (
            <Card className="border-slate-200 bg-white shadow-sm" data-testid="link-picker-card">
              <CardHeader className="py-3">
                <CardTitle className="text-sm text-slate-700">
                  Você tem {linkOptions.length} links ativos. Escolha qual deseja compartilhar:
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                <div className="flex flex-wrap gap-2">
                  {linkOptions.map((ln, idx) => {
                    const selected = idx === selectedLinkIdx;
                    return (
                      <button
                        key={ln.id || ln.token}
                        type="button"
                        onClick={() => setSelectedLinkIdx(idx)}
                        className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
                          selected
                            ? 'border-transparent text-white shadow-sm'
                            : 'border-slate-200 text-slate-700 hover:border-slate-400 bg-white'
                        }`}
                        style={selected ? { background: tenantColor } : undefined}
                        data-testid={`link-picker-option-${idx}`}
                      >
                        {ln.label || ln.token}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== Bloco 1: Convite apoiadores ===== */}
          <Card className="border-2 border-emerald-200 bg-white shadow-sm overflow-hidden">
            <CardHeader className="bg-emerald-50/80 border-b border-emerald-200 py-3">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center">
                  <Users className="h-4 w-4" />
                </div>
                <div>
                  <CardTitle className="text-base text-emerald-900">Convide apoiadores</CardTitle>
                  <CardDescription className="text-xs text-emerald-700/80">
                    Quem entrar por este link contará como apoio seu.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                {liveQrDataUrl && (
                  <div className="rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 p-3 shrink-0">
                    <img
                      src={liveQrDataUrl}
                      alt="QR Code de captação de apoiadores"
                      className="w-36 h-36"
                      data-testid="leader-supporter-qr"
                    />
                  </div>
                )}
                <div className="flex-1 w-full space-y-2.5">
                  <div className="flex gap-2">
                    <Input
                      value={liveShareUrl}
                      readOnly
                      className="font-mono text-xs h-10"
                      data-testid="leader-supporter-link-url"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={copyLink}
                      className="h-10 w-10 shrink-0"
                      data-testid="copy-supporter-link-btn"
                    >
                      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <a
                      href={supporterWa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors shadow-sm"
                      data-testid="share-supporter-wa-btn"
                    >
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </a>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-10 border-emerald-200"
                      onClick={() => downloadQR('supporter')}
                      data-testid="download-supporter-qr-btn"
                    >
                      <Download className="h-4 w-4 mr-1.5" /> Salvar QR
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== Bloco 2: Convite lideranças ===== */}
          {liveInviteUrl && (
            <Card className="border-2 border-violet-200 bg-white shadow-sm overflow-hidden">
              <CardHeader className="bg-violet-50/80 border-b border-violet-200 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-lg bg-violet-500 text-white flex items-center justify-center">
                    <Share2 className="h-4 w-4" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-violet-900">Convide novas lideranças</CardTitle>
                    <CardDescription className="text-xs text-violet-700/80">
                      Multiplique sua rede! Quem entrar por aqui também vira liderança.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  {liveInviteQrDataUrl && (
                    <div className="rounded-xl border-2 border-dashed border-violet-200 bg-violet-50/50 p-3 shrink-0">
                      <img
                        src={liveInviteQrDataUrl}
                        alt="QR Code de captação de lideranças"
                        className="w-36 h-36"
                        data-testid="leader-invite-qr"
                      />
                    </div>
                  )}
                  <div className="flex-1 w-full space-y-2.5">
                    <div className="flex gap-2">
                      <Input
                        value={liveInviteUrl}
                        readOnly
                        className="font-mono text-xs h-10"
                        data-testid="leader-invite-link-url"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={copyInviteLink}
                        className="h-10 w-10 shrink-0"
                        data-testid="copy-invite-link-btn"
                      >
                        {copiedInvite ? <Check className="h-4 w-4 text-violet-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        href={inviteWa}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-violet-500 text-white text-sm font-semibold hover:bg-violet-600 transition-colors shadow-sm"
                        data-testid="share-invite-wa-btn"
                      >
                        <MessageCircle className="h-4 w-4" /> WhatsApp
                      </a>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-10 border-violet-200"
                        onClick={() => downloadQR('invite')}
                        data-testid="download-invite-qr-btn"
                      >
                        <Download className="h-4 w-4 mr-1.5" /> Salvar QR
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== Bloco 3: Seguir no canal (menor destaque) ===== */}
          {destinationUrl && (
            <a
              href={destinationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors border border-slate-200 p-4 group"
              data-testid="follow-channel-link"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 group-hover:text-slate-900 transition-colors">
                    <MessageCircle className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      Seguir no canal
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {destinationName || 'Acesse o destino final'}
                    </div>
                  </div>
                </div>
                <div className="text-slate-400 group-hover:text-slate-900 transition-colors shrink-0">
                  →
                </div>
              </div>
            </a>
          )}
        </div>
      </div>
    );
  }

  // ===== Render: form =====
  const tenantColor = info?.primary_color || '#1e3a8a';
  return (
    <div
      className="min-h-screen p-6"
      style={{ background: `linear-gradient(135deg, ${tenantColor}10, ${tenantColor}05)` }}
    >
      <div className="max-w-xl mx-auto pt-8">
        <Card className="border-slate-200 overflow-hidden">
          {info?.signup_hero_image_url && (
            <div className="w-full bg-slate-100 overflow-hidden">
              <img
                src={info.signup_hero_image_url}
                alt={info?.tenant_name || 'Banner'}
                className="w-full h-44 object-cover"
                data-testid="signup-hero-image"
              />
            </div>
          )}
          <CardHeader className="text-center">
            {!info?.signup_hero_image_url && (
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center mx-auto mb-3 text-white font-bold"
                style={{ background: tenantColor }}
              >
                {info?.tenant_name?.charAt(0) || 'T'}
              </div>
            )}
            <CardTitle className="text-2xl" data-testid="signup-title">
              {info?.signup_title || (mode === 'invite' ? 'Cadastro de Liderança (por indicação)' : 'Cadastro de Liderança')}
            </CardTitle>
            <CardDescription className="text-base" data-testid="signup-subtitle">
              {info?.signup_subtitle || (
                mode === 'invite' && info?.inviter ? (
                  <>
                    Você foi indicado(a) por <strong>{info.inviter.apelido || info.inviter.nome}</strong> para ser uma liderança em <strong>{info?.tenant_name}</strong>.
                    Após o cadastro, você receberá um link personalizado para convidar apoiadores.
                  </>
                ) : (
                  <>
                    Você está se cadastrando como liderança em <strong>{info?.tenant_name}</strong>.
                    Após o cadastro, você receberá um link personalizado para convidar apoiadores.
                  </>
                )
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode !== 'invite' && (
              <div className="mb-5">
                <button
                  type="button"
                  onClick={openRecovery}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                  data-testid="open-recovery-top-btn"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="h-8 w-8 rounded-lg flex items-center justify-center text-white shrink-0"
                      style={{ background: tenantColor }}
                    >
                      <KeyRound className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">Já sou liderança</div>
                      <div className="text-xs text-slate-500 truncate">
                        Recupere seus QR codes e links de convite com seu telefone.
                      </div>
                    </div>
                  </div>
                  <span className="text-slate-400 shrink-0">→</span>
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* === Seção 1: Dados pessoais === */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-1 border-b border-slate-200">
                  <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">
                    Seus dados
                  </h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nome_completo">Nome completo *</Label>
                  <Input
                    id="nome_completo"
                    value={formData.nome_completo}
                    onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                    placeholder="Seu nome completo"
                    required
                    data-testid="signup-nome-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apelido">Apelido / Como prefere ser chamado(a)</Label>
                  <Input
                    id="apelido"
                    value={formData.apelido}
                    onChange={(e) => setFormData({ ...formData, apelido: e.target.value })}
                    placeholder="Ex: João, Tia Maria, Dr. José"
                    data-testid="signup-apelido-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">WhatsApp *</Label>
                  <PhoneInput
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    required
                    data-testid="signup-telefone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data de nascimento</Label>
                  <Input
                    id="data_nascimento"
                    type="text"
                    value={formData.data_nascimento}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                      let masked = digits;
                      if (digits.length > 4) {
                        masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
                      } else if (digits.length > 2) {
                        masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                      }
                      setFormData({ ...formData, data_nascimento: masked });
                    }}
                    placeholder="dd/mm/aaaa"
                    inputMode="numeric"
                    autoComplete="bday"
                    maxLength={10}
                    className="h-11 text-base"
                    data-testid="signup-data-nascimento-input"
                  />
                </div>
              </div>

              {/* === Seção 2: Região de atuação === */}
              <div className="space-y-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center gap-2 pb-1 border-b border-primary/20">
                  <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">
                    Onde você atua como liderança
                  </h3>
                </div>
                <p className="text-xs text-slate-600 -mt-2 leading-relaxed">
                  Informe a região <strong>onde você atua e mobiliza pessoas</strong> hoje — não onde você nasceu.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="estado">Estado de atuação *</Label>
                    <select
                      id="estado"
                      value={formData.estado}
                      onChange={(e) =>
                        setFormData({ ...formData, estado: e.target.value, cidade: '' })
                      }
                      className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="signup-estado-select"
                    >
                      <option value="">UF</option>
                      {estadosVisiveis.map((e) => (
                        <option key={e.sigla} value={e.sigla}>
                          {e.sigla} — {e.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="cidade">Cidade de atuação *</Label>
                    <select
                      id="cidade"
                      value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      disabled={!formData.estado}
                      className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="signup-cidade-select"
                    >
                      <option value="">
                        {formData.estado ? 'Selecione a cidade...' : 'Escolha o estado primeiro'}
                      </option>
                      {cidadesDoEstado.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro / Região de atuação</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    placeholder="Ex: Centro, Vila Nova — onde você se mobiliza"
                    className="bg-white"
                    data-testid="signup-bairro-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tipo">Tipo de liderança *</Label>
                  <select
                    id="tipo"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid="signup-tipo-select"
                  >
                    <option value="">Selecione...</option>
                    {tiposValidos.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações (opcional)</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  placeholder="Algo que queira nos contar sobre sua atuação..."
                  rows={3}
                />
              </div>

              {error && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="signup-error">
                  {error}
                </div>
              )}

              {/* Honeypot — invisible to humans, bots fill it */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-5000px', height: 0, width: 0, overflow: 'hidden' }}>
                <label htmlFor="website">Site (deixe em branco)</label>
                <input
                  id="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>

              <Button
                type="submit"
                className="w-full text-base py-6"
                disabled={submitting}
                style={{ background: tenantColor }}
                data-testid="signup-submit-btn"
              >
                {submitting ? 'Cadastrando...' : 'Finalizar cadastro'}
              </Button>
              <p className="text-xs text-slate-500 text-center pt-2">
                Ao se cadastrar você confirma que deseja participar voluntariamente da rede.
              </p>
              {mode !== 'invite' && (
                <p className="text-xs text-center pt-1">
                  <button
                    type="button"
                    onClick={openRecovery}
                    className="text-slate-600 underline-offset-4 hover:underline font-medium"
                    data-testid="open-recovery-bottom-btn"
                  >
                    Já é uma liderança? Recupere seus links
                  </button>
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
      {/* ===== Recovery dialog ("Já sou liderança") ===== */}
      <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
        <DialogContent className="sm:max-w-md" data-testid="recovery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" style={{ color: tenantColor }} />
              Já sou liderança
            </DialogTitle>
            <DialogDescription>
              Informe o telefone usado no seu cadastro para recuperar seus QR codes e links de convite.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRecovery} className="space-y-4 pt-1">
            <div className="space-y-2">
              <Label htmlFor="recovery-telefone">WhatsApp cadastrado</Label>
              <PhoneInput
                id="recovery-telefone"
                value={recoveryPhone}
                onChange={(e) => setRecoveryPhone(e.target.value)}
                required
                autoFocus
                data-testid="recovery-telefone-input"
              />
            </div>
            {recoveryError && (
              <div
                className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3"
                data-testid="recovery-error"
              >
                {recoveryError}
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRecoveryOpen(false)}
                data-testid="recovery-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={recoverySubmitting}
                style={{ background: tenantColor }}
                className="text-white"
                data-testid="recovery-submit-btn"
              >
                {recoverySubmitting ? 'Buscando...' : 'Recuperar meus links'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
